import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TestModeService } from '../services/test-mode';

/**
 * Live / Test VIEW switcher in the topbar.
 *
 * It does not change anyone's mode — that now belongs to each client and partner, so the system
 * is never wholly live or wholly test. This only chooses which records the dashboards and lists
 * show, and the choice is remembered per browser.
 *
 * Admin-only; rendered only when the caller decides.
 */
@Component({
  selector: 'app-mode-switch',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="ms" [class.test]="svc.viewTest()" role="group" aria-label="Data view">
      <button type="button" class="ms-seg" [class.on]="!svc.viewTest()"
        (click)="pick(false)" title="Show live records">
        <span class="dot live"></span> Live
      </button>
      <button type="button" class="ms-seg" [class.on]="svc.viewTest()"
        (click)="pick(true)" title="Show test records">
        <span class="dot test"></span> Test
      </button>
    </div>
  `,
  styles: [`
    :host { display: inline-flex; }
    .ms {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      padding: 3px;
      border-radius: 10px;
      border: 1px solid var(--line, #e7ecef);
      background: #f8fafc;
    }
    .ms.test { border-color: #f59e0b; background: #fffbeb; }

    .ms-seg {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      border: 0;
      background: transparent;
      font: inherit;
      font-size: 12.5px;
      font-weight: 600;
      color: var(--muted, #6b7280);
      padding: 6px 11px;
      border-radius: 8px;
      cursor: pointer;
      white-space: nowrap;
      transition: background .16s var(--ease-out), color .16s var(--ease-out);
    }
    .ms-seg:disabled { opacity: .6; cursor: default; }
    .ms-seg:not(:disabled):hover { color: var(--text, #1f2937); }

    /* The selected side carries the colour; the other stays quiet. */
    .ms-seg.on { background: #fff; color: #15803d; box-shadow: 0 1px 3px rgba(15,23,42,.12); }
    .ms.test .ms-seg.on { color: #b45309; }

    .dot { width: 7px; height: 7px; border-radius: 50%; background: #cbd5e1; }
    .ms-seg.on .dot.live { background: #16a34a; }
    .ms-seg.on .dot.test { background: #d97706; }

    @media (max-width: 900px) {
      .ms-seg { padding: 6px 9px; font-size: 12px; }
    }
  `],
})
export class ModeSwitch {
  readonly svc = inject(TestModeService);
  private router = inject(Router);

  /**
   * Changes only what is displayed — no server call, nothing switched on for anyone else.
   * The current page is reloaded through the router so its data request goes out again with
   * the new view.
   */
  pick(test: boolean) {
    if (test === this.svc.viewTest()) return;
    this.svc.setView(test);
    const url = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true })
      .then(() => this.router.navigateByUrl(url));
  }
}
